import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import { formIDRegex, formNameRegex } from '../../utils';
import { AddNewItemForm } from '../BaseComponents';
import { DataKeys } from '../BaseComponents/AddNewItemForm';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators } from '../../reducers/templateEditor';
import { actionCreators as pagesActionCreators } from '../../reducers/pages';

const idRegex = formIDRegex;
const nameRegex = formNameRegex;

type FormData = coreTypes.SavePageFormData;

const AddNewPageForm = ({ urlState }: { urlState?: string }) => {
    const dispatch = useDispatch();
    const i18n = commonHooks.useLocalization();

    // initial form data
    const initialData = {
        id: '',
        name: '',
    };
    const [data, setData] = React.useState<FormData>(initialData);

    const [selectedTemplate, setSelectedTemplate] = React.useState<string | undefined>(undefined);

    // error messages for invalid inputs
    const [dataError, setDataError] = React.useState<FormData>(initialData);

    // error message on page save
    const [error, setError] = React.useState<string>('');

    // success message on page save
    const [success, setSuccess] = React.useState<string>('');

    const { pageLoading, saveNewPageLoading, pageSaveSuccess, pageSaveError, templates } =
        useSelector((state: ReduxStateShape) => ({
            ...state?.templateEditor,
            templates: state.adminPages.templates,
        }));

    const fetchTemplates = async () => {
        await dispatch(pagesActionCreators.loadTemplates());
    };

    useEffect(() => {
        fetchTemplates();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (pageSaveSuccess) {
            setSuccess(pageSaveSuccess);
            dispatch(pagesActionCreators.loadPages(urlState || ''));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSaveSuccess]);

    useEffect(() => {
        if (pageSaveError) {
            setError(i18n.t('page_form_save_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSaveError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: DataKeys) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && dataError[field]) {
            setDataError({ ...dataError, [field]: '' });
        }

        if (field === 'id') {
            if (value) {
                if (!idRegex.test(value)) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_id') });
                } else if (value.length > 32) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_id_length') });
                } else {
                    setDataError({ ...dataError, id: '' });
                }
            } else {
                setDataError({ ...dataError, id: i18n.t('form_missing_id') });
            }
        }

        if (field === 'name') {
            if (value) {
                if (nameRegex.test(value))
                    setDataError({ ...dataError, name: i18n.t('form_invalid_name') });
                else if (value.length > 64)
                    setDataError({ ...dataError, name: i18n.t('form_invalid_name_length') });
                else setDataError({ ...dataError, name: '' });
            } else {
                setDataError({ ...dataError, name: i18n.t('form_missing_name') });
            }
        }

        setData({
            ...data,
            [field]: value,
        });
    };

    const validateFormData = () => {
        let valid = false;
        const { id, name } = data;
        const err: FormData = {
            id: '',
            name: '',
            description: '',
        };

        if (!id) {
            err.id = i18n.t('form_missing_id');
        }

        if (!name) {
            err.name = i18n.t('form_missing_name');
        }

        if (!_.values(err).every(_.isEmpty)) setDataError({ ...dataError, ...err });

        valid =
            _.every(err, (e) => e === '' || _.isNull(e)) &&
            _.every(dataError, (e) => e === '' || _.isNull(e));
        return valid;
    };

    const handleSubmit = async () => {
        const isFormValid = validateFormData();

        if (isFormValid) {
            // check if page with the same name exists
            const doc = await dispatch(
                actionCreators.loadDocument(
                    urlState ? `${urlState.substring(1)}/${data.id}` : data.id
                )
            );

            if (!_.isEmpty(doc)) {
                setError(i18n.t('page_already_exists'));
                setSuccess('');
            } else {
                await dispatch(
                    actionCreators.saveNewPage(
                        urlState ? `${urlState.substring(1)}/${data.id}` : data.id,
                        selectedTemplate
                    )
                );

                setData(initialData);
                setDataError(initialData);
            }
        }
    };

    const templateNames = templates?.map((t) => t.name);

    return (
        <>
            <AddNewItemForm
                formTitle="Create New Page"
                data={data}
                dataError={dataError}
                templates={templateNames as string[]}
                handleInputChange={handleInputChange}
                handleSelectTemplate={(template) => setSelectedTemplate(template)}
                handleSubmit={handleSubmit}
                showDescription={false}
                saveError={error}
                saveSuccess={success}
                showSaveLoading={pageLoading || saveNewPageLoading || false}
                defaultActiveAccordionKey="0"
            />
        </>
    );
};

export default AddNewPageForm;
